import { format } from "date-fns";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  resolveOrderLoadStatus,
  describeLoadStatusSource,
  type HistoricalLoadStatus,
} from "@/lib/order-load-status";

interface Props {
  /** Order/leg status — delivered rows show the at-delivery value. */
  status?: string | null;
  /** Current `trailers.load_status` for this row's trailer. */
  liveLoadStatus?: string | null;
  /** At-delivery snapshot from `order_load_status_at_delivery`, if any. */
  historical?: HistoricalLoadStatus | null;
  className?: string;
}

function formatWhen(iso: string) {
  try {
    return format(new Date(iso), "MMM dd 'at' hh:mm a");
  } catch {
    return iso;
  }
}

/**
 * Renders the trailer's load status for an order row. Delivered orders show the
 * value as it was at delivery rather than the trailer's live state, which can
 * belong to a completely different trip by now.
 */
export function OrderLoadStatusCell({ status, liveLoadStatus, historical, className }: Props) {
  const resolved = resolveOrderLoadStatus({ status, liveLoadStatus, historical });

  if (!resolved.value) {
    return <span className={cn("text-muted-foreground", className)}>—</span>;
  }

  const tooltip = describeLoadStatusSource(resolved, formatWhen);

  const content = (
    <span className={cn("capitalize", className)}>
      {resolved.value}
      {resolved.isHistorical && (
        <span className="ml-1 text-xs text-muted-foreground">at delivery</span>
      )}
    </span>
  );

  if (!tooltip) return content;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-default">{content}</span>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}
